import {
  InputSelectionListChangeEvent,
  SelectionList,
  Text,
  TextWithHighlight,
} from '@dixa/lib-shared-habla';
import { HookFormSelectionList } from 'lib-shared-hook-form';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import {
  CustomFieldSelectAttribute,
  CustomFieldSelectAttributeOption,
  CustomFieldStringListAttributeValue,
} from 'apis/customFields';
import { convertTreeToFlatItems } from 'utils/tree';

import { CustomFieldDisplayValue } from '../customFieldDisplayValue/customFieldDisplayValue';
import { BaseCustomFieldProps } from '../customFields.types';

import styles from './baseCustomFieldSelect.module.scss';

type BaseCustomFieldSelectProps = BaseCustomFieldProps<
  CustomFieldSelectAttribute,
  CustomFieldStringListAttributeValue,
  Partial<Record<string, (readonly string[])[]>>
> & {
  /** Custom field input on change method */
  readonly onChange?: (
    event: InputSelectionListChangeEvent<string[]>,
  ) => Promise<void>;
  /** Whether to show the label line */
  readonly noLabel?: boolean;
  /** Whether to show the alert line */
  readonly noAlert?: boolean;
};

type CustomFieldSelectAttributeFlatOption = Omit<
  CustomFieldSelectAttributeOption,
  'value'
> & {
  value: string[];
};

const pathSeparator = ' / ';

export const BaseCustomFieldSelect = memo(function BaseCustomFieldSelect({
  customField,
  onChange,
  control,
  name,
  rules,
  warning,
  componentProps,
  noLabel,
  noAlert,
}: BaseCustomFieldSelectProps): JSX.Element {
  const { attribute } = customField;
  const { id, options, label, description } = attribute;
  const [filter, setFilter] = useState('');
  const fieldName = name ?? id;
  const formValue = useWatch({ control, name: fieldName });

  const helpIcon = useMemo(
    () => ({
      text: description,
    }),
    [description],
  );

  const getItemTitleByValue = useCallback(() => {
    return (
      <CustomFieldDisplayValue
        customField={customField}
        config={{
          pathSeparator,
          formValue: formValue?.at(0) as string[] | undefined,
        }}
      />
    );
  }, [customField, formValue]);

  const renderListOrItem = useCallback(
    (option: CustomFieldSelectAttributeOption, valuePath: string[] = []) => {
      if (option.options?.length) {
        return (
          <SelectionList.List
            key={option.id}
            parentItem={
              <SelectionList.Item>
                <Text.Paragraph>{option.label}</Text.Paragraph>
              </SelectionList.Item>
            }
          >
            {[...option.options]
              .filter(({ is_deactivated }) => !is_deactivated)
              .map((childOption) =>
                renderListOrItem(childOption, [...valuePath, option.value]),
              )}
          </SelectionList.List>
        );
      }

      return (
        <SelectionList.Item
          key={option.id}
          value={[...valuePath, option.value]}
        >
          <Text.Paragraph>{option.label}</Text.Paragraph>
        </SelectionList.Item>
      );
    },
    [],
  );

  const renderListItems = useCallback(
    () =>
      [...options]
        .filter(({ is_deactivated }) => !is_deactivated)
        .map((option) => renderListOrItem(option)),
    [options, renderListOrItem],
  );

  const inputSelectionListProps = useMemo(
    () => ({
      ...componentProps,
      clearable: !rules?.required,
      getItemTitleByValue,
      onChange,
      equalityFn: isEqual,
      filter: {
        value: filter,
        onChange: (event) => {
          setFilter(event.target.value);
        },
      },
      onToggleDropdown: (isOpen: boolean) => isOpen && setFilter(''),
      selectionListProps: {
        className: styles.selectionList,
      },
    }),
    [filter, componentProps, rules, getItemTitleByValue, onChange],
  );

  const renderFilteredListItems = useCallback(
    () =>
      convertTreeToFlatItems(options, {
        childrenKey: 'options',
        transformItem: (
          option,
          path,
        ): CustomFieldSelectAttributeFlatOption => ({
          ...option,
          label: path
            .map(({ label }) => label)
            .concat([option.label])
            .join(pathSeparator),
          value: path.map(({ value }) => value).concat([option.value]),
        }),
      })
        .filter(
          ({ is_deactivated, label, options: childOptions }) =>
            !is_deactivated &&
            !childOptions?.length &&
            label.toLowerCase().includes(filter.toLowerCase()),
        )
        .map((option) => (
          <SelectionList.Item key={option.id} value={option.value}>
            <Text.Paragraph>
              <TextWithHighlight text={option.label} highlight={filter} />
            </Text.Paragraph>
          </SelectionList.Item>
        )),
    [filter, options],
  );

  return (
    <HookFormSelectionList
      control={control}
      label={noLabel ? undefined : label}
      noLabel={noLabel}
      noAlert={noAlert}
      name={fieldName}
      rules={rules}
      helpIcon={noLabel ? undefined : helpIcon}
      componentProps={inputSelectionListProps}
      meta={{ warning }}
    >
      {filter ? renderFilteredListItems() : renderListItems()}
    </HookFormSelectionList>
  );
});
